/**
 * Manually-maintained SDK functions mirroring the OpenAPI contracts.
 *
 * Phase 3 ships these by hand because @hey-api/openapi-ts v0.55 has a
 * plugin-ecosystem mismatch with our tooling. A follow-up spec
 * replaces this file with auto-generated content from
 * `make generate-client`.
 */

import type {
  AdminEstimate,
  AdminOfferBody,
  AdminUserSummary,
  AnchorKind,
  ApiError,
  AuthMe,
  Role,
  ChatMessage,
  DepositCheckoutResponse,
  Design,
  Estimate,
  EstimateCreateRequest,
  EstimatePatchBody,
  EstimateSnapshot,
  EstimateStatus,
  ImportCommitResponse,
  ImportPresignResponse,
  ImportPreviewResponse,
  LaborPatchBody,
  LaborRate,
  LineItemOverride,
  LineItemOverrideBody,
  Material,
  MaterialCreateBody,
  MaterialPatchBody,
  Package,
  PackageApplyResult,
  PackageCard,
  PackageCreateBody,
  PackagePatchBody,
  MaterialSelection,
  Note,
  PageMeta,
  Photo,
  PhotoCategory,
  ArLinearPayload,
  ArSurfacePayload,
  PresignResponse,
  RemodelIntent,
  RemodelIntentCreateBody,
  RemodelIntentPatchBody,
  RejectionCategory,
  RemodelRender,
  RenderStatus,
  RoomPlanPayload,
  ScaleAnchor,
  TenantBranding,
  TenantSummary,
  ZipFactor,
  ZipFactorPatchBody,
} from "./types.gen";
import { request } from "./client";

export async function getTenantBranding(opts: {
  query: { subdomain: string };
}): Promise<{ data: TenantBranding } | { error: ApiError }> {
  return request("GET", "/v1/tenants/me/branding", { query: opts.query });
}

export async function getAuthMe(): Promise<
  { data: AuthMe } | { error: ApiError }
> {
  return request("GET", "/v1/auth/me", { auth: true });
}

export async function postAuthSignout(): Promise<
  { data: { signed_out: boolean } } | { error: ApiError }
> {
  return request("POST", "/v1/auth/signout", { auth: true, body: {} });
}

export async function getPlatformTenants(): Promise<
  { data: TenantSummary[] } | { error: ApiError }
> {
  return request("GET", "/v1/platform/tenants", { auth: true });
}

// ---- spec 002: Estimate + Chat ----

export async function createEstimate(opts: {
  body: EstimateCreateRequest;
  idempotencyKey?: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request("POST", "/v1/estimates", {
    auth: true,
    body: opts.body,
    headers: opts.idempotencyKey
      ? { "Idempotency-Key": opts.idempotencyKey }
      : undefined,
  });
}

export async function listEstimates(opts?: {
  cursor?: string;
  limit?: number;
}): Promise<
  { data: Estimate[]; meta?: PageMeta } | { error: ApiError }
> {
  return request("GET", "/v1/estimates", {
    auth: true,
    query: { cursor: opts?.cursor, limit: opts?.limit },
    includeMeta: true,
  });
}

export async function getEstimate(opts: {
  estimateId: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request("GET", `/v1/estimates/${opts.estimateId}`, { auth: true });
}

export async function listEstimateMessages(opts: {
  estimateId: string;
  cursor?: string;
  limit?: number;
}): Promise<
  { data: ChatMessage[]; meta?: PageMeta } | { error: ApiError }
> {
  return request("GET", `/v1/estimates/${opts.estimateId}/messages`, {
    auth: true,
    query: { cursor: opts.cursor, limit: opts.limit },
    includeMeta: true,
  });
}

// Spec 014 — inline measure: mark a measure_payload as saved after the
// customer completes an AR capture from the chat MeasureButton.
export async function patchChatMessageMeasure(opts: {
  estimateId: string;
  sequence: number;
  savedAnchorId: string;
  savedSummary: string;
}): Promise<{ data: ChatMessage } | { error: ApiError }> {
  return request(
    "PATCH",
    `/v1/estimates/${opts.estimateId}/messages/${opts.sequence}/measure`,
    {
      auth: true,
      body: {
        saved_anchor_id: opts.savedAnchorId,
        saved_summary: opts.savedSummary,
      },
    },
  );
}

export async function listAdminEstimates(opts?: {
  status?: EstimateStatus;
  cursor?: string;
  limit?: number;
}): Promise<
  { data: AdminEstimate[]; meta?: PageMeta } | { error: ApiError }
> {
  return request("GET", "/v1/admin/estimates", {
    auth: true,
    query: {
      status: opts?.status,
      cursor: opts?.cursor,
      limit: opts?.limit,
    },
    includeMeta: true,
  });
}

// ---- spec 003: identity, photos, anchors ----

export async function patchEstimate(opts: {
  estimateId: string;
  ifMatchVersion: number;
  body: EstimatePatchBody;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request("PATCH", `/v1/estimates/${opts.estimateId}`, {
    auth: true,
    body: opts.body,
    headers: { "If-Match": String(opts.ifMatchVersion) },
  });
}

// Customer soft-deletes (archives) an in_progress project. Backend
// returns 409 when the estimate has moved past in_progress.
export async function deleteEstimate(opts: {
  estimateId: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request("DELETE", `/v1/estimates/${opts.estimateId}`, { auth: true });
}

export async function presignPhoto(opts: {
  estimateId: string;
  mimeType: "image/jpeg" | "image/png";
  bytes: number;
  category?: PhotoCategory;
}): Promise<{ data: PresignResponse } | { error: ApiError }> {
  return request("POST", `/v1/estimates/${opts.estimateId}/photos:presign`, {
    auth: true,
    body: {
      mime_type: opts.mimeType,
      bytes: opts.bytes,
      ...(opts.category ? { category: opts.category } : {}),
    },
  });
}

export async function finalizePhoto(opts: {
  estimateId: string;
  photoId: string;
}): Promise<{ data: Photo } | { error: ApiError }> {
  return request("POST", `/v1/estimates/${opts.estimateId}/photos:finalize`, {
    auth: true,
    body: { photo_id: opts.photoId },
  });
}

export async function listPhotos(opts: {
  estimateId: string;
}): Promise<{ data: Photo[] } | { error: ApiError }> {
  return request("GET", `/v1/estimates/${opts.estimateId}/photos`, {
    auth: true,
  });
}

export async function deletePhoto(opts: {
  estimateId: string;
  photoId: string;
}): Promise<{ data: { deleted: boolean } } | { error: ApiError }> {
  return request(
    "DELETE",
    `/v1/estimates/${opts.estimateId}/photos/${opts.photoId}`,
    { auth: true },
  );
}

export async function createAnchor(opts: {
  estimateId: string;
  kind: AnchorKind;
  valueText: string;
  tiedToPhotoId?: string | null;
  roomPlanPayload?: RoomPlanPayload | null;
  arLinearPayload?: ArLinearPayload | null;
  arSurfacePayload?: ArSurfacePayload | null;
}): Promise<{ data: ScaleAnchor } | { error: ApiError }> {
  return request("POST", `/v1/estimates/${opts.estimateId}/anchors`, {
    auth: true,
    body: {
      kind: opts.kind,
      value_text: opts.valueText,
      tied_to_photo_id: opts.tiedToPhotoId ?? null,
      room_plan_payload: opts.roomPlanPayload ?? null,
      ar_linear_payload: opts.arLinearPayload ?? null,
      ar_surface_payload: opts.arSurfacePayload ?? null,
    },
  });
}

export async function listAnchors(opts: {
  estimateId: string;
}): Promise<{ data: ScaleAnchor[] } | { error: ApiError }> {
  return request("GET", `/v1/estimates/${opts.estimateId}/anchors`, {
    auth: true,
  });
}

// Phase 17 polish — customer hard-deletes a recorded measurement.
// Allowed only while the estimate is still `in_progress` — backend
// returns 409 after submission.
export async function deleteAnchor(opts: {
  estimateId: string;
  anchorId: string;
}): Promise<
  { data: { anchor_id: string; deleted: boolean } } | { error: ApiError }
> {
  return request(
    "DELETE",
    `/v1/estimates/${opts.estimateId}/anchors/${opts.anchorId}`,
    { auth: true },
  );
}

// ---- Spec 004: catalog admin ----

export async function listMaterials(opts?: {
  optionGroupId?: string;
  active?: boolean;
}): Promise<{ data: Material[] } | { error: ApiError }> {
  return request("GET", "/v1/admin/catalog/materials", {
    auth: true,
    query: {
      option_group_id: opts?.optionGroupId,
      active: opts?.active,
    },
  });
}

// Spec 016 — single-material create. 409 when material_id exists.
export async function createMaterial(opts: {
  body: MaterialCreateBody;
}): Promise<{ data: Material } | { error: ApiError }> {
  return request("POST", "/v1/admin/catalog/materials", {
    auth: true,
    body: opts.body,
  });
}

export async function patchMaterial(opts: {
  materialId: string;
  ifMatchVersion: number;
  body: MaterialPatchBody;
}): Promise<{ data: Material } | { error: ApiError }> {
  return request("PATCH", `/v1/admin/catalog/materials/${opts.materialId}`, {
    auth: true,
    body: opts.body,
    headers: { "If-Match": String(opts.ifMatchVersion) },
  });
}

export async function setMaterialActive(opts: {
  materialId: string;
  active: boolean;
}): Promise<{ data: Material } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/admin/catalog/materials/${opts.materialId}/active`,
    { auth: true, body: { active: opts.active } },
  );
}

export async function listLabor(): Promise<
  { data: LaborRate[] } | { error: ApiError }
> {
  return request("GET", "/v1/admin/catalog/labor", { auth: true });
}

// Spec 016 — labor rate inline edit. Honors If-Match optimistic
// concurrency; 409 on a stale version.
export async function patchLabor(opts: {
  taskId: string;
  ifMatchVersion: number;
  body: LaborPatchBody;
}): Promise<{ data: LaborRate } | { error: ApiError }> {
  return request("PATCH", `/v1/admin/catalog/labor/${opts.taskId}`, {
    auth: true,
    body: opts.body,
    headers: { "If-Match": String(opts.ifMatchVersion) },
  });
}

export async function listZipFactors(): Promise<
  { data: ZipFactor[] } | { error: ApiError }
> {
  return request("GET", "/v1/admin/catalog/zip-factors", { auth: true });
}

// Spec 016 — ZIP factor inline edit. Honors If-Match.
export async function patchZipFactor(opts: {
  zip: string;
  ifMatchVersion: number;
  body: ZipFactorPatchBody;
}): Promise<{ data: ZipFactor } | { error: ApiError }> {
  return request("PATCH", `/v1/admin/catalog/zip-factors/${opts.zip}`, {
    auth: true,
    body: opts.body,
    headers: { "If-Match": String(opts.ifMatchVersion) },
  });
}

// Spec 016 — presign a catalog photo upload. Returns the S3 PUT URL
// plus the public CloudFront URL to save on the material's photo_url.
export async function presignCatalogPhoto(opts: {
  mimeType: "image/jpeg" | "image/png";
  bytes: number;
  materialId: string;
}): Promise<
  | { data: { upload_url: string; photo_url: string; expires_at: string } }
  | { error: ApiError }
> {
  return request("POST", "/v1/admin/catalog/photos:presign", {
    auth: true,
    body: {
      mime_type: opts.mimeType,
      bytes: opts.bytes,
      material_id: opts.materialId,
    },
  });
}

// Phase 17 — RemodelIntent CRUD (kickoff cards seeded on fresh chat).
export async function listRemodelIntents(): Promise<
  { data: RemodelIntent[] } | { error: ApiError }
> {
  return request("GET", "/v1/admin/catalog/intents", { auth: true });
}

export async function createRemodelIntent(opts: {
  body: RemodelIntentCreateBody;
}): Promise<{ data: RemodelIntent } | { error: ApiError }> {
  return request("POST", "/v1/admin/catalog/intents", {
    auth: true,
    body: opts.body,
  });
}

export async function patchRemodelIntent(opts: {
  intentId: string;
  ifMatchVersion: number;
  body: RemodelIntentPatchBody;
}): Promise<{ data: RemodelIntent } | { error: ApiError }> {
  return request("PATCH", `/v1/admin/catalog/intents/${opts.intentId}`, {
    auth: true,
    body: opts.body,
    headers: { "If-Match": String(opts.ifMatchVersion) },
  });
}

// ---- Phase 21: bathroom project packages ----

/**
 * The project-type choices for one estimate (customer-facing). Returns a
 * reduced projection — no `line_item_material_ids`. Empty when the
 * estimate's room type has no active packages, in which case the wizard
 * shows no project-type step.
 */
export async function listEstimatePackages(opts: {
  estimateId: string;
}): Promise<
  | { data: PackageCard[]; meta?: Record<string, unknown> }
  | { error: ApiError }
> {
  return request("GET", `/v1/estimates/${opts.estimateId}/packages`, {
    auth: true,
  });
}

/**
 * Seed the estimate's selections from a package. Clears the slots a
 * previously applied package filled that this one does not cover, then
 * upserts each item into its own option-group slot.
 *
 * `idempotencyKey` is optional — re-applying the same package converges
 * on the same rows anyway, so a double-tap is already safe.
 */
export async function applyEstimatePackage(opts: {
  estimateId: string;
  packageId: string;
  idempotencyKey?: string;
}): Promise<{ data: PackageApplyResult } | { error: ApiError }> {
  return request("POST", `/v1/estimates/${opts.estimateId}/package:apply`, {
    auth: true,
    body: { package_id: opts.packageId },
    headers: opts.idempotencyKey
      ? { "Idempotency-Key": opts.idempotencyKey }
      : undefined,
  });
}

// Phase 21 — tenant-admin package management.
export async function listPackages(opts?: {
  activeOnly?: boolean;
}): Promise<
  { data: Package[]; meta?: Record<string, unknown> } | { error: ApiError }
> {
  return request("GET", "/v1/admin/catalog/packages", {
    auth: true,
    query: { active_only: opts?.activeOnly },
  });
}

export async function createPackage(opts: {
  body: PackageCreateBody;
}): Promise<{ data: Package } | { error: ApiError }> {
  return request("POST", "/v1/admin/catalog/packages", {
    auth: true,
    body: opts.body,
  });
}

export async function patchPackage(opts: {
  packageId: string;
  ifMatchVersion: number;
  body: PackagePatchBody;
}): Promise<{ data: Package } | { error: ApiError }> {
  return request("PATCH", `/v1/admin/catalog/packages/${opts.packageId}`, {
    auth: true,
    body: opts.body,
    headers: { "If-Match": String(opts.ifMatchVersion) },
  });
}

export async function setPackageActive(opts: {
  packageId: string;
  active: boolean;
}): Promise<{ data: Package } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/admin/catalog/packages/${opts.packageId}/active`,
    { auth: true, body: { active: opts.active } },
  );
}

// Phase 17 — admin sets/replaces the active offer on an estimate.
// 409 on version mismatch; 403 if caller is not a tenant admin.
export async function setAdminOffer(opts: {
  estimateId: string;
  ifMatchVersion: number;
  body: AdminOfferBody;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/admin/estimates/${opts.estimateId}/offer`,
    {
      auth: true,
      body: opts.body,
      headers: { "If-Match": String(opts.ifMatchVersion) },
    },
  );
}

// Phase 17 — re-price an expired quote against the current catalog.
// 409 if the estimate is not in EXPIRED status.
export async function refreshEstimate(opts: {
  estimateId: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/estimates/${opts.estimateId}/refresh`,
    { auth: true, body: {} },
  );
}

export async function presignCatalogImport(opts: {
  kind: "material" | "labor" | "zip_factor";
  bytes: number;
}): Promise<{ data: ImportPresignResponse } | { error: ApiError }> {
  return request("POST", "/v1/admin/catalog/import:presign", {
    auth: true,
    body: { kind: opts.kind, bytes: opts.bytes },
  });
}

export async function previewCatalogImport(opts: {
  importId: string;
  kind: "material" | "labor" | "zip_factor";
}): Promise<{ data: ImportPreviewResponse } | { error: ApiError }> {
  return request("POST", "/v1/admin/catalog/import:preview", {
    auth: true,
    body: { import_id: opts.importId, kind: opts.kind },
  });
}

export async function commitCatalogImport(opts: {
  importId: string;
}): Promise<{ data: ImportCommitResponse } | { error: ApiError }> {
  return request("POST", "/v1/admin/catalog/import:commit", {
    auth: true,
    body: { import_id: opts.importId },
  });
}

export async function getEstimateSnapshot(opts: {
  estimateId: string;
}): Promise<{ data: EstimateSnapshot } | { error: ApiError }> {
  return request("GET", `/v1/estimates/${opts.estimateId}/snapshot`, {
    auth: true,
  });
}

export async function listEstimateSelections(opts: {
  estimateId: string;
}): Promise<{ data: MaterialSelection[] } | { error: ApiError }> {
  return request("GET", `/v1/estimates/${opts.estimateId}/selections`, {
    auth: true,
  });
}

// ---- spec 005: lifecycle ----

export async function submitEstimate(opts: {
  estimateId: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request("POST", `/v1/estimates/${opts.estimateId}/submit`, {
    auth: true,
  });
}

export async function startReviewEstimate(opts: {
  estimateId: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/admin/estimates/${opts.estimateId}/start-review`,
    { auth: true },
  );
}

export async function overrideLineItem(opts: {
  estimateId: string;
  lineId: string;
  body: LineItemOverrideBody;
}): Promise<{ data: EstimateSnapshot } | { error: ApiError }> {
  return request(
    "PATCH",
    `/v1/admin/estimates/${opts.estimateId}/line-items/${opts.lineId}`,
    { auth: true, body: opts.body },
  );
}

export async function approveEstimate(opts: {
  estimateId: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/admin/estimates/${opts.estimateId}/approve`,
    { auth: true },
  );
}

export async function rejectEstimate(opts: {
  estimateId: string;
  body: { reason: string };
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request("POST", `/v1/admin/estimates/${opts.estimateId}/reject`, {
    auth: true,
    body: opts.body,
  });
}

export async function listOverrides(opts: {
  estimateId: string;
}): Promise<{ data: LineItemOverride[] } | { error: ApiError }> {
  return request(
    "GET",
    `/v1/admin/estimates/${opts.estimateId}/overrides`,
    { auth: true },
  );
}

export async function createDepositCheckout(opts: {
  estimateId: string;
}): Promise<{ data: DepositCheckoutResponse } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/estimates/${opts.estimateId}/deposit:checkout`,
    { auth: true },
  );
}

// ---- spec 006: admin notes + clarifying questions ----

export async function listNotes(opts: {
  estimateId: string;
}): Promise<{ data: Note[] } | { error: ApiError }> {
  return request("GET", `/v1/admin/estimates/${opts.estimateId}/notes`, {
    auth: true,
  });
}

export async function createNote(opts: {
  estimateId: string;
  body: { body: string };
}): Promise<{ data: Note } | { error: ApiError }> {
  return request("POST", `/v1/admin/estimates/${opts.estimateId}/notes`, {
    auth: true,
    body: opts.body,
  });
}

export async function updateNote(opts: {
  estimateId: string;
  noteId: string;
  body: { body: string };
}): Promise<{ data: Note } | { error: ApiError }> {
  return request(
    "PATCH",
    `/v1/admin/estimates/${opts.estimateId}/notes/${opts.noteId}`,
    { auth: true, body: opts.body },
  );
}

export async function deleteNote(opts: {
  estimateId: string;
  noteId: string;
}): Promise<{ data: { deleted: boolean } } | { error: ApiError }> {
  return request(
    "DELETE",
    `/v1/admin/estimates/${opts.estimateId}/notes/${opts.noteId}`,
    { auth: true },
  );
}

export async function askClarification(opts: {
  estimateId: string;
  body: { question: string };
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/admin/estimates/${opts.estimateId}/ask-clarification`,
    { auth: true, body: opts.body },
  );
}

export async function resumeReview(opts: {
  estimateId: string;
}): Promise<{ data: Estimate } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/admin/estimates/${opts.estimateId}/resume-review`,
    { auth: true },
  );
}

// ---- spec 007: design sketch ----

export async function getEstimateDesign(opts: {
  estimateId: string;
}): Promise<{ data: Design } | { error: ApiError }> {
  return request("GET", `/v1/estimates/${opts.estimateId}/design`, {
    auth: true,
  });
}

// ---- Phase 18: generative remodel previews ----

export async function listRenders(opts: {
  estimateId: string;
  status?: RenderStatus;
}): Promise<
  | {
      data: RemodelRender[];
      meta?: Record<string, unknown>;
    }
  | { error: ApiError }
> {
  return request("GET", `/v1/estimates/${opts.estimateId}/renders`, {
    auth: true,
    query: opts.status ? { status: opts.status } : undefined,
    includeMeta: true,
  });
}

export async function approveRender(opts: {
  estimateId: string;
  renderId: string;
}): Promise<{ data: RemodelRender } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/estimates/${opts.estimateId}/renders/${opts.renderId}/approve`,
    { auth: true },
  );
}

export async function rejectRender(opts: {
  estimateId: string;
  renderId: string;
  // Phase 18.1 — optional feedback body. When omitted the reject still
  // succeeds; Stella just has no signal beyond "this one was rejected."
  reason?: string;
  category?: RejectionCategory;
}): Promise<{ data: RemodelRender } | { error: ApiError }> {
  const hasFeedback = opts.reason !== undefined || opts.category !== undefined;
  return request(
    "POST",
    `/v1/estimates/${opts.estimateId}/renders/${opts.renderId}/reject`,
    {
      auth: true,
      body: hasFeedback
        ? { reason: opts.reason, category: opts.category }
        : undefined,
    },
  );
}

export async function deleteRender(opts: {
  estimateId: string;
  renderId: string;
}): Promise<
  { data: { render_id: string; deleted: boolean } } | { error: ApiError }
> {
  return request(
    "DELETE",
    `/v1/estimates/${opts.estimateId}/renders/${opts.renderId}`,
    { auth: true },
  );
}

// ---- Phase 18.4: approved sketch as render reference ----

export async function listDesigns(opts: { estimateId: string }): Promise<
  { data: Design[]; meta?: Record<string, unknown> } | { error: ApiError }
> {
  return request("GET", `/v1/estimates/${opts.estimateId}/designs`, {
    auth: true,
    includeMeta: true,
  });
}

export async function approveDesign(opts: {
  estimateId: string;
  designId: string;
}): Promise<{ data: Design } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/estimates/${opts.estimateId}/designs/${opts.designId}/approve`,
    { auth: true },
  );
}

export async function rejectDesign(opts: {
  estimateId: string;
  designId: string;
}): Promise<{ data: Design } | { error: ApiError }> {
  return request(
    "POST",
    `/v1/estimates/${opts.estimateId}/designs/${opts.designId}/reject`,
    { auth: true },
  );
}

// ---- Tenant-admin user management ----

export async function listAdminUsers(): Promise<
  { data: AdminUserSummary[] } | { error: ApiError }
> {
  return request("GET", "/v1/admin/users", { auth: true });
}

export async function inviteAdmin(opts: {
  email: string;
  firstName?: string;
  lastName?: string;
}): Promise<{ data: AdminUserSummary } | { error: ApiError }> {
  return request("POST", "/v1/admin/users/invite", {
    auth: true,
    body: {
      email: opts.email,
      first_name: opts.firstName ?? "",
      last_name: opts.lastName ?? "",
    },
  });
}

export async function setUserRole(opts: {
  username: string;
  role: Role;
}): Promise<{ data: AdminUserSummary } | { error: ApiError }> {
  return request("POST", "/v1/admin/users/set-role", {
    auth: true,
    body: { username: opts.username, role: opts.role },
  });
}

export async function resetUserPassword(opts: {
  username: string;
}): Promise<{ data: { reset: boolean } } | { error: ApiError }> {
  return request("POST", "/v1/admin/users/reset-password", {
    auth: true,
    body: { username: opts.username },
  });
}

export async function setUserStatus(opts: {
  username: string;
  enabled: boolean;
}): Promise<{ data: AdminUserSummary } | { error: ApiError }> {
  return request("POST", "/v1/admin/users/set-status", {
    auth: true,
    body: { username: opts.username, enabled: opts.enabled },
  });
}
